import { useContext, useState } from 'react'
import { Keyboard, View } from 'react-native'
import { useMutation, useQuery } from '@apollo/client'
import { useRootNavigationState } from 'expo-router'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { REQUEST_SET_USER_WITHDRAWAL_PASSWORD, SET_USER_WITHDRAWAL_PASSWORD } from '@/constants/graphql/mutations'
import { GET_USER } from '@/constants/graphql/queries'
import { useRouter } from '@/utils/navigation'
import { AuthContext } from '@/stores/auth'
import { Button, FormScrollView, useToast } from '@/components/base'
import { OtpForm } from '@/components/form'
import { TxInput } from '@/components/form-control'
import { Stack } from '@/components/layout'
import { useLoadingIndicator } from '@/components/LoadingIndicator'
import NavigationBar from '@/components/NavigationBar'
import { SelectPaymentMethodContext } from '@/components/SelectPaymentMethodScreen'

export type SetTxPasswordStep = 'setPassword' | 'verify'

type txPasswordValues = {
  txPassword: string
  confirmTxPassword: string
  verifyCode: string
}

const defaultValues: txPasswordValues = {
  txPassword: '',
  confirmTxPassword: '',
  verifyCode: ''
}

export default function TxPasswordScreen() {
  const { t, i18n } = useTranslation()
  const router = useRouter()
  const state = useRootNavigationState()
  const { show, isHidden } = useContext(SelectPaymentMethodContext)
  const user = AuthContext.useSelector((state) => state.context.values?.user)

  const { data: userData } = useQuery(GET_USER, {
    variables: {
      id: user?.id
    }
  })
  const action = userData?.user?.withdrawalPasswordEnabled ? 'reset' : 'set'

  const { control, handleSubmit, watch, setError } = useForm<txPasswordValues>({ defaultValues })
  const loading = useLoadingIndicator()
  const toast = useToast()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const txPassword = watch('txPassword')
  const confirmTxPassword = watch('confirmTxPassword')
  const verifyCode = watch('verifyCode')
  const isAddingPaymentButForgotPassword =
    state.routes.length >= 2 && state.routes[1].name === '(main)/setting/payment-method/add'

  const [requestOTP] = useMutation(REQUEST_SET_USER_WITHDRAWAL_PASSWORD, {
    variables: {
      locale: i18n.language,
      withdrawalPassword: txPassword
    },
    onError: (error) => {
      console.log('error', error)
      const { graphQLErrors } = error
      const errorCode = graphQLErrors[0]?.extensions?.code
      throw new Error(String(errorCode ?? error.message))
    }
  })

  const [setUserWithdrawalPassword] = useMutation(SET_USER_WITHDRAWAL_PASSWORD, {
    onCompleted: (data) => {
      loading.stop()
      if (data.setUserWithdrawalPassword) {
        // router.navigate('/(main)/(tabs)/my')
        router.back()
        // HACK: avoid showing select payment method screen when user adds payment method but forgets withdrawal password
        if (!isAddingPaymentButForgotPassword && isHidden) {
          show()
        }
        toast.show({
          variant: 'success',
          description: t(action === 'set' ? 'settings:transactionPasswordSetSuccess' : 'settings:transactionPasswordChangedSuccess')
        })
      } else {
        toast.show({
          variant: 'warning',
          description: t(action === 'set' ? 'settings:transactionPasswordSetFailed' : 'settings:transactionPasswordChangedFailed')
        })
      }
    },
    onError: (error) => {
      loading.stop()
      console.log('error', error)
      const { graphQLErrors } = error
      const errorCode = graphQLErrors[0]?.extensions?.code

      toast.show({ variant: 'error', description: t('settings:transactionPasswordSetFailedWithCode', { code: errorCode }) })
    },
    refetchQueries: [GET_USER]
  })

  const onOpenOTPModal = () => {
    Keyboard.dismiss()
    if (txPassword !== confirmTxPassword) {
      setError('confirmTxPassword', { message: t('auth:hint.mismatch') })
      return
    }
    setIsModalOpen(true)
    // loading.start()
    requestOTP()
  }

  const onSubmit = async (formValues: txPasswordValues) => {
    console.log('submitting!')
    Keyboard.dismiss()
    loading.start()
    setUserWithdrawalPassword({
      variables: {
        password: formValues.txPassword,
        token: formValues.verifyCode
      }
    })
  }

  if (isModalOpen) {
    return (
      <>
        <Stack.Screen
          options={{
            header: () => (
              <NavigationBar iconId={'x'} handleAction={() => setIsModalOpen(false)}>
                {t('ui:securityVerification')}
              </NavigationBar>
            )
          }}
        />
        {/* <Animated.View className={'absolute size-full'} style={[viewStyle]}> */}
        <OtpForm control={control} onSubmit={handleSubmit(onSubmit)} requestOTP={requestOTP} verifyCode={verifyCode} />
        {/* </Animated.View> */}
      </>
    )
  }

  return (
    <>
      <Stack.Screen
        options={{
          header: () => (
            <NavigationBar
              handleAction={() => {
                router.back()
                if (!isAddingPaymentButForgotPassword && isHidden) {
                  show()
                }
              }}
            >
              {t(action === 'set' ? 'settings:setTransactionPassword' : 'settings:changeTransactionPassword')}
            </NavigationBar>
          )
        }}
      />
      <FormScrollView className={'flex-1'} contentContainerStyle={{ flexGrow: 1 }}>
        <View className={'grow bg-background-secondary p-4'}>
          <View className={'rounded-lg border border-border-10 bg-background-primary p-4'}>
            <TxInput
              control={control}
              name={'txPassword'}
              label={t(action === 'set' ? 'auth:label.txPassword' : 'settings:newTransactionPassword')}
            />
            <View className={'h-2'} />
            <TxInput control={control} name={'confirmTxPassword'} label={t('settings:confirmTransactionPassword')} />
            <View className={'h-4'} />
            <Button
              size={'md'}
              intent={'primary'}
              variant={'solid'}
              onPress={onOpenOTPModal}
              disabled={txPassword.length !== 6 || confirmTxPassword.length !== 6 || loading.isLoading}
            >
              {t('ui:confirm')}
            </Button>
          </View>
          <View className={'h-4'} />
        </View>
      </FormScrollView>
    </>
  )
}
